
import React, {Component} from 'react';
import styled from 'styled-components';
import {Grid, Row, Col as UnstyledCol} from 'react-flexbox-grid';
import {SortableContainer, SortableElement, arrayMove} from 'react-sortable-hoc';

import * as api from '../../lib/api';
import {withUser} from '../../lib/user';
import QuestionChart from '../../components/QuestionChart';
import Button from '../../components/Button';
import Icon from '../../components/Icon';
import TimeAgo from '../../components/TimeAgo';
import Loading from '../../components/Loading';
import Error from '../../components/Error';

class DashboardWidgetSet extends Component {
  state = {
    loading: true,
    error: false,
    pinnedWidgets: null,
    fullscreen: false
  };

  componentDidMount() {
    this.load();
  }

  componentWillUnmount() {
    if (this.handle) {
      clearTimeout(this.handle);
    }
  }

  load = async () => {
    const {pinnedWidgets} = this.state;
    const {credentials} = this.props;

    try {
      const pinnedWidgets = reverseNumeric(this.getWidgets(await api.getPinnedWidgets(credentials)));
      this.setState({pinnedWidgets, lastUpdate: new Date()});
    } 
    catch (e) {
      // If pinnedWidgets is null, it means we have never loaded any pinned widgets before so we can show
      // an error. If there is already pinned widgets loaded, then this is a background data refresh and
      // we don't want to destroy the last successfully loaded data.
      if (pinnedWidgets === null) {
        this.setState({error: true});
      }
    }
    finally {
      this.setState({loading: false});
    }

    // Poll for updates every minute.
    this.handle = setTimeout(() => this.load(), 60000);
  }

  handleSortWidgets = async ({oldIndex, newIndex}) => {
    const {credentials, user} = this.props;
    const { pinnedWidgets } = this.state;
    const sortedWidgets = arrayMove(pinnedWidgets, oldIndex, newIndex).map((widget, pinnedOrder) => ({...widget, pinnedOrder}));

    const sortOrderById = sortedWidgets.reduce((map, widget) => {
      map[widget.id] = widget.pinnedOrder;
      return map;
    }, {});

    this.setState({
      pinnedWidgets: sortedWidgets,
    });

    const request = {
      widgets: pinnedWidgets.reduce((map, question) => {
        map[question.id] = sortOrderById[question.id];
        return map;
      }, {})
    };

    api.sortPinnedWidgets(user.id, request, credentials);
  }

  unPinWidget = widgetToPin => {
    const {credentials} = this.props;
    const {pinnedWidgets} = this.state;
    widgetToPin.pinned = false;

    this.setState({
      pinnedWidgets: pinnedWidgets.map(q => ({...q, pinned: q.id === widgetToPin.id ? false: q.pinned})).filter(widget => widget.pinned)
    });

    api.unPinWidget(widgetToPin, credentials)
  }

  getWidgets(pinnedWidgets) {
    const {user, templates} = this.props;
    return pinnedWidgets.map(question => ({
      type: QuestionChart,
      id: question.id,
      order: question.widgetOrder,
      pinnedOrder: question.pinnedWidgetOrder,
      hidden: question.hidden,
      pinned: question.pinned,
      question,
      answers: question.answers,
      user: user,
      dashboardWidgetSet: true,
      unPin: this.unPinWidget,
      templates,
      filters: question.filters,
      sheetIds: question.sheetIds,
      allSheetsPinned: question.allSheetsPinned,
      parentTemplateId: question.parentTemplateId
    }));
  }

  render() {
    const {loading, error, pinnedWidgets, fullscreen, lastUpdate} = this.state;
    const {user} = this.props;
    let items = (pinnedWidgets || []).sort((a, b) => a.pinnedOrder - b.pinnedOrder);

    return (
      <>
        {(user.role !== "USER") &&
        <PinnedWidgetHeader>
          <SectionHeader>Pinned Widgets</SectionHeader>
          <PinnedWidgetOptions>
            <Button secondary onClick={() => this.setState({fullscreen: true})}>
              View Live <Icon name="external-link" />
            </Button>
          </PinnedWidgetOptions>
        </PinnedWidgetHeader>}
        <FullScreenView expanded={fullscreen}>
          {fullscreen && (
            <FullScreenOptions>
              {lastUpdate && (
                <LastUpdate>
                  Dashboard last updated <strong>&nbsp;<TimeAgo date={lastUpdate} /></strong>
                </LastUpdate>
              )}
              <Button secondary onClick={() => this.setState({fullscreen: false})}>
                Leave Fullscreen <Icon name="minimize" />
              </Button>
            </FullScreenOptions>
          )}
          {loading ? (
            <Loading message="Loading your pinned widgets..." />
          ) : error ? (
            <Error message="Something went wrong while loading your pinned widgets." />
          ) : (items.length < 1 && user.role !== "USER")? (
            <NoWidgets>You haven't pinned any widgets yet!</NoWidgets>
          ) : (
            <Widgets
              useDragHandle
              distance={5}
              axis="xy"
              list={items}
              onSortEnd={this.handleSortWidgets}
            />
          )}
        </FullScreenView>
      </>
    );
  }
}

function reverseNumeric(items) {
  items.forEach(item => {
    if(item.question != null) {
      if(item.question.type === "NUMERIC" || item.question.type === "MEASURED_NUMERIC" || item.question.type === "MULTI_NUMERIC"){
        item.question.answers.reverse()
      }
    }
  })
  return items;
}

const Widgets = SortableContainer(({list}) => (
  <Grid fluid>
    <Row>
      {list.map((item, index) => (
        <Widget {...item} key={item.id} index={index} />
      ))}
    </Row>
  </Grid>
));

const Widget = SortableElement(({type: WidgetType, id, ...props}) => (
  <Col xs={12} sm={12} md={12} lg={6}>
    <div id={id}>
      <WidgetType {...props} />
    </div>
  </Col>
));

const Col = styled(UnstyledCol)`
  padding: 0.9375rem !important;
`;

const PinnedWidgetHeader = styled.div`
  display: flex;
  margin-top: 32px;
`;

const SectionHeader = styled.h1`
  margin-bottom: 0.5rem;
  white-space: nowrap; 
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;

  @media (max-width: 800px) {
    font-size: 2.25rem;
  }
`;

const PinnedWidgetOptions = styled.div`
  display: flex;
  align-items: center;
`;

const FullScreenView = styled.div`
  ${props => props.expanded && `
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: #f0f0f3;
    overflow: auto;
    padding: 16px;
  `}
`;

const FullScreenOptions = styled.div`
  padding: 8px;
  display: flex;
  justify-content: flex-end;
`;

const LastUpdate = styled.div`
  display: flex;
  align-items: center;
  margin-right: 16px;
`;

const NoWidgets = styled.p`
  text-align: center;
  font-weight: 500;
  margin: 16px 0;
  color: #747474;
  line-height: 1.5;
`;

export default withUser(DashboardWidgetSet);
